import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check, CheckCircle2, Circle, Copy,
  ExternalLink, Eye, EyeOff, Key, Lock, ShieldCheck, Zap,
} from 'lucide-react';
import { useAuth } from '@/lib/auth/useAuth';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { fetchMyClient, generateMyApiKey, type GenerateApiKeyResult } from '@/features/clients/api';
import type { ResponseEnvelope } from '@/types/api';
import type { DashboardRole } from '@/types/auth';

const CLIENT_ADMIN: DashboardRole = 'AUTHMATECH_CLIENT_ADMIN';

interface MyProduct {
  productCode: string;
  displayName: string;
  clientEnabled: boolean;
}

async function fetchMyProducts(): Promise<MyProduct[]> {
  const res = await api.get<ResponseEnvelope<MyProduct[]>>(Endpoints.me.products);
  return unwrap(res);
}

// Product badge colours — full class strings so Tailwind picks them up at scan time
const PRODUCT_BADGE: Record<string, string> = {
  VERIFY:             'bg-blue-500/15 text-blue-300 border-blue-500/25',
  VERIFY_PLUS:        'bg-indigo-500/15 text-indigo-300 border-indigo-500/25',
  SIGNAL:             'bg-violet-500/15 text-violet-300 border-violet-500/25',
  SHIELD:             'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  DETECT:             'bg-orange-500/15 text-orange-300 border-orange-500/25',
  TRUST:              'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
  STUCK_PLUS:         'bg-rose-500/15 text-rose-300 border-rose-500/25',
  STUCK_PLUS_REVEAL:  'bg-pink-500/15 text-pink-300 border-pink-500/25',
  CONNECT:            'bg-teal-500/15 text-teal-300 border-teal-500/25',
  CONNECT_ENRICHMENT: 'bg-green-500/15 text-green-300 border-green-500/25',
};

// ── One-time API key reveal modal ─────────────────────────────────────────────

function ApiKeyRevealModal({
  result,
  onClose,
}: {
  result: GenerateApiKeyResult;
  onClose: () => void;
}) {
  const [showKey, setShowKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  function copy(text: string, setter: (v: boolean) => void) {
    void navigator.clipboard.writeText(text).then(() => {
      setter(true);
      setTimeout(() => setter(false), 3000);
    });
  }

  const cardBorder = { border: '1px solid rgba(96,165,250,0.18)' };
  const sectionBorder = { borderBottom: '1px solid rgba(96,165,250,0.10)' };
  const fieldBase = { border: '1px solid rgba(96,165,250,0.14)', background: 'rgba(255,255,255,0.03)' };
  const fieldHighlight = { border: '1px solid rgba(96,165,250,0.30)', background: 'rgba(37,99,235,0.06)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl"
        style={{
          background: '#0b1220',
          ...cardBorder,
          boxShadow: '0 0 0 1px rgba(96,165,250,0.06), 0 24px 80px rgba(0,0,0,0.7)',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5" style={sectionBorder}>
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)' }}
          >
            <ShieldCheck className="h-5 w-5" style={{ color: '#34d399' }} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">API key generated</h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.40)' }}>
              Copy this key now — it will not be shown again.
            </p>
          </div>
        </div>

        {/* Warning */}
        <div
          className="mx-6 mt-5 rounded-xl px-4 py-3 text-sm font-medium"
          style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.22)', color: '#fbbf24' }}
        >
          Store this API key securely. Once you leave this page it{' '}
          <span className="font-bold">cannot be recovered</span>.
        </div>

        {/* Fields */}
        <div className="space-y-4 px-6 py-5">
          {/* Client ID */}
          <div>
            <p
              className="mb-1.5 text-xs font-semibold uppercase tracking-wider"
              style={{ color: 'rgba(255,255,255,0.28)' }}
            >
              Client ID
            </p>
            <div className="flex items-center gap-2 rounded-xl px-3 py-2.5" style={fieldBase}>
              <p
                className="flex-1 min-w-0 font-mono text-xs break-all"
                style={{ color: 'rgba(255,255,255,0.65)' }}
              >
                {result.clientId}
              </p>
              <button
                type="button"
                onClick={() => copy(result.clientId, setCopiedId)}
                className="shrink-0 rounded p-1 transition-colors"
                style={{ color: 'rgba(255,255,255,0.30)' }}
                title="Copy Client ID"
              >
                {copiedId
                  ? <Check className="h-4 w-4" style={{ color: '#34d399' }} />
                  : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* API Key */}
          <div>
            <p
              className="mb-1.5 text-xs font-semibold uppercase tracking-wider"
              style={{ color: 'rgba(255,255,255,0.28)' }}
            >
              API Key
            </p>
            <div className="flex items-center gap-2 rounded-xl px-3 py-2.5" style={fieldHighlight}>
              <p
                className="flex-1 min-w-0 font-mono text-xs break-all"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                {showKey ? result.rawApiKey : '•'.repeat(44)}
              </p>
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="shrink-0 rounded p-1 transition-colors"
                style={{ color: 'rgba(255,255,255,0.30)' }}
                title={showKey ? 'Hide key' : 'Reveal key'}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => copy(result.rawApiKey, setCopiedKey)}
                className="shrink-0 rounded p-1 transition-colors"
                style={{ color: 'rgba(255,255,255,0.30)' }}
                title="Copy API key"
              >
                {copiedKey
                  ? <Check className="h-4 w-4" style={{ color: '#34d399' }} />
                  : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.22)' }}>
            Use as <span className="font-mono">X-API-KEY</span> header with{' '}
            <span className="font-mono">X-CLIENT-ID</span> in all API requests.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-5" style={{ borderTop: '1px solid rgba(96,165,250,0.10)' }}>
          <button
            type="button"
            onClick={() => copy(result.rawApiKey, setCopiedKey)}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition-all active:scale-[0.98]"
            style={{ background: '#2563EB', color: '#FFFFFF', boxShadow: '0 0 20px rgba(37,99,235,0.30)' }}
          >
            {copiedKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copiedKey ? 'Copied!' : 'Copy API Key'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition-all active:scale-[0.98]"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(96,165,250,0.15)',
              color: 'rgba(255,255,255,0.75)',
            }}
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Activation checklist step ─────────────────────────────────────────────────

function ActivationStep({
  label,
  done,
  pending,
}: {
  label: string;
  done: boolean;
  pending?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      {done ? (
        <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: '#34d399' }} />
      ) : pending ? (
        <div
          className="h-4 w-4 shrink-0 rounded-full"
          style={{ border: '1.5px dashed rgba(96,165,250,0.55)' }}
        />
      ) : (
        <Circle className="h-4 w-4 shrink-0" style={{ color: 'rgba(255,255,255,0.15)' }} />
      )}
      <span
        className="text-sm"
        style={{
          color: done
            ? '#34d399'
            : pending
              ? 'rgba(255,255,255,0.72)'
              : 'rgba(255,255,255,0.22)',
        }}
      >
        {label}
        {pending && (
          <span className="ml-2 text-xs" style={{ color: 'rgba(96,165,250,0.65)' }}>
            — pending
          </span>
        )}
      </span>
    </div>
  );
}

// ── Main activation screen ────────────────────────────────────────────────────

/**
 * Premium full-page activation experience for ONBOARDING clients.
 * Rendered by ClientOverviewPage when clientStatus === 'ONBOARDING'.
 * Invisible to all Authmatech internal/admin roles — the route guard
 * on /portal/overview prevents admin access, and the role check here
 * adds defense-in-depth.
 */
export function ActivationWorkspaceScreen() {
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<GenerateApiKeyResult | null>(null);

  const clientQuery = useQuery({
    queryKey: ['me', 'myClient'],
    queryFn: fetchMyClient,
    staleTime: 30_000,
  });

  const productsQuery = useQuery({
    queryKey: ['me', 'products'],
    queryFn: fetchMyProducts,
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation({
    mutationFn: generateMyApiKey,
    onSuccess: (data) => {
      // Store result — do NOT invalidate here. Invalidation happens only when
      // the user explicitly clicks "Continue to Dashboard". If we invalidate
      // now, clientStatus flips to ACTIVE, ClientOverviewPage stops rendering
      // ActivationWorkspaceScreen, and the modal unmounts before the user can
      // copy the key — which is shown exactly once.
      setResult(data);
    },
  });

  const canGenerate = hasRole(CLIENT_ADMIN);
  const client = clientQuery.data;
  const enabledProducts = (productsQuery.data ?? []).filter((p) => p.clientEnabled);

  const error = mutation.error
    ? isApiError(mutation.error)
      ? mutation.error.firstErrorMessage
      : 'Failed to generate API key. Please try again.'
    : null;

  return (
    <>
      {result && (
        <ApiKeyRevealModal
          result={result}
          onClose={() => {
            setResult(null);
            void queryClient.invalidateQueries({ queryKey: ['me', 'myClient'] });
          }}
        />
      )}

      {/* Centre in available content area */}
      <div className="flex min-h-[calc(100vh-9rem)] items-center justify-center py-10">
        <div className="w-full max-w-2xl">

          {/* Hero card */}
          <div
            className="relative overflow-hidden rounded-3xl"
            style={{
              background: 'linear-gradient(140deg, #060e1e 0%, #081325 55%, #0c1b3c 100%)',
              border: '1px solid rgba(96,165,250,0.16)',
              boxShadow:
                '0 0 0 1px rgba(96,165,250,0.05), 0 28px 90px rgba(37,99,235,0.16)',
            }}
          >
            {/* Background glows */}
            <div
              className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(96,165,250,0.09) 0%, transparent 65%)' }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-20 -left-12 h-60 w-60 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.06) 0%, transparent 65%)' }}
              aria-hidden
            />

            <div className="relative px-8 py-9 sm:px-10 sm:py-10">

              {/* Top bar: brand + company tag */}
              <div className="mb-8 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{
                      background: 'rgba(96,165,250,0.12)',
                      border: '1px solid rgba(96,165,250,0.22)',
                    }}
                  >
                    <Zap className="h-4.5 w-4.5" style={{ color: '#60a5fa' }} />
                  </div>
                  <span className="text-sm font-semibold text-white">Authmatech</span>
                </div>

                {client?.clientName && (
                  <span
                    className="rounded-full px-3 py-1 text-xs font-medium"
                    style={{
                      background: 'rgba(96,165,250,0.08)',
                      border: '1px solid rgba(96,165,250,0.16)',
                      color: 'rgba(255,255,255,0.50)',
                    }}
                  >
                    {client.clientName}
                  </span>
                )}
              </div>

              {/* Heading */}
              <h1 className="text-[28px] font-bold leading-tight text-white sm:text-3xl">
                Activate your Authmatech<br />workspace
              </h1>
              <p
                className="mt-3 text-base leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.50)' }}
              >
                Your workspace is ready. Generate your API key to start using your
                enabled Authmatech products.
              </p>
              <p
                className="mt-2 text-sm leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.30)' }}
              >
                This key authenticates API requests from your systems. It will be displayed
                only once — copy it and store it securely.
              </p>

              {/* Divider */}
              <div
                className="my-7"
                style={{ borderTop: '1px solid rgba(96,165,250,0.09)' }}
              />

              {/* Two-column: checklist + products */}
              <div className="grid gap-4 sm:grid-cols-2">

                {/* Activation checklist */}
                <div
                  className="rounded-2xl p-5"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(96,165,250,0.09)',
                  }}
                >
                  <p
                    className="mb-4 text-[11px] font-semibold uppercase tracking-widest"
                    style={{ color: 'rgba(255,255,255,0.25)' }}
                  >
                    Activation progress
                  </p>
                  <div className="space-y-3">
                    <ActivationStep label="Workspace created" done />
                    <ActivationStep label="Password updated" done />
                    <ActivationStep label="API key generation" done={false} pending />
                    <ActivationStep label="First integration test" done={false} />
                  </div>
                </div>

                {/* Enabled products */}
                <div
                  className="rounded-2xl p-5"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(96,165,250,0.09)',
                  }}
                >
                  <p
                    className="mb-4 text-[11px] font-semibold uppercase tracking-widest"
                    style={{ color: 'rgba(255,255,255,0.25)' }}
                  >
                    Enabled products
                  </p>

                  {productsQuery.isLoading ? (
                    <div className="space-y-2">
                      {[75, 55, 65].map((w) => (
                        <div
                          key={w}
                          className="h-6 animate-pulse rounded-full"
                          style={{ width: `${w}%`, background: 'rgba(255,255,255,0.05)' }}
                        />
                      ))}
                    </div>
                  ) : enabledProducts.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {enabledProducts.map((p) => {
                        const cls =
                          PRODUCT_BADGE[p.productCode] ??
                          'bg-white/10 text-white/50 border-white/15';
                        return (
                          <span
                            key={p.productCode}
                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`}
                          >
                            {p.displayName}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p
                      className="text-sm"
                      style={{ color: 'rgba(255,255,255,0.22)' }}
                    >
                      Contact Authmatech to enable products on your account.
                    </p>
                  )}
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div
                  className="mt-5 rounded-xl px-4 py-3 text-sm"
                  style={{
                    background: 'rgba(239,68,68,0.07)',
                    border: '1px solid rgba(239,68,68,0.22)',
                    color: '#f87171',
                  }}
                >
                  {error}
                </div>
              )}

              {/* CTA area */}
              <div className="mt-7 flex flex-wrap items-center gap-4">
                {canGenerate ? (
                  <button
                    type="button"
                    onClick={() => mutation.mutate()}
                    disabled={mutation.isPending}
                    className="inline-flex items-center gap-2.5 rounded-xl px-7 py-3.5 text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
                    style={{
                      background: '#2563EB',
                      boxShadow: '0 0 28px rgba(37,99,235,0.38)',
                    }}
                  >
                    {mutation.isPending ? (
                      <>
                        <svg
                          className="h-4 w-4 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden
                        >
                          <circle
                            cx="12" cy="12" r="10"
                            stroke="currentColor" strokeWidth="4"
                            className="opacity-25"
                          />
                          <path
                            fill="currentColor"
                            className="opacity-75"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Generating…
                      </>
                    ) : (
                      <>
                        <Key className="h-4 w-4" />
                        Generate API Key
                      </>
                    )}
                  </button>
                ) : (
                  <div
                    className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
                    style={{
                      background: 'rgba(251,191,36,0.07)',
                      border: '1px solid rgba(251,191,36,0.18)',
                      color: '#fbbf24',
                    }}
                  >
                    <Lock className="h-4 w-4 shrink-0" />
                    API key generation requires Client Admin role.
                    Contact your account administrator.
                  </div>
                )}

                <a
                  href="https://service.authmatech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm transition-opacity hover:opacity-80"
                  style={{ color: 'rgba(96,165,250,0.65)' }}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Documentation
                </a>
              </div>

              {/* Footer: signed-in user info */}
              {user?.email && (
                <p
                  className="mt-8 text-xs"
                  style={{ color: 'rgba(255,255,255,0.18)' }}
                >
                  Signed in as {user.email} · Need help?{' '}
                  <a
                    href="mailto:support@authmatech.com"
                    className="underline underline-offset-2"
                    style={{ color: 'rgba(255,255,255,0.28)' }}
                  >
                    support@authmatech.com
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
